import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { type S3SignedUrl, useGetDocumentUrlMutation } from "@/mutations/document.ts";
import { useDeleteMedia } from "@/mutations/useDeleteMedia.tsx";
import type { Media } from "@/queries/useFetchMediaForCase.ts";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import {
    Box,
    CircularProgress,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";

type Props = {
    mediaArray: Media[];
    limitToCategory?: string;
    showDelete?: boolean;
};

const MediaDocumentsListRaw = ({ mediaArray, limitToCategory, showDelete = true }: Props) => {
    const { hasPermission } = useEmployee();
    const getDocumentUrlMutation = useGetDocumentUrlMutation();
    const { mutate: getDocumentUrl } = getDocumentUrlMutation;
    const deleteMedia = useDeleteMedia();
    const [mediaUUID, setMediaUUID] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);

        if (mediaUUID) {
            getDocumentUrl(mediaUUID, {
                onSuccess: (data: S3SignedUrl) => {
                    if (data.url) {
                        window.open(data.url, "_blank");
                    }

                    setLoading(false);
                    setMediaUUID(undefined);
                },
                onError: () => {
                    setLoading(false);
                    setMediaUUID(undefined);
                },
            });
        }
    }, [mediaUUID, getDocumentUrl]);

    const filteredDocuments = useMemo(() => {
        if (limitToCategory) {
            return mediaArray.filter((mediaItem: Media) => {
                return mediaItem.tag?.toLowerCase().includes(limitToCategory.toLowerCase());
            });
        }

        return mediaArray;
    }, [limitToCategory, mediaArray]);

    const handleDeleteMedia = (mediaUUID: string) => {
        if (window.confirm("Are you sure you want to delete this Document?")) {
            deleteMedia.mutate(
                {
                    mediaUUID: mediaUUID,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Document removed.", { variant: "success" });
                    },
                    onError: () => {
                        enqueueSnackbar("Error: Document could not be removed.", {
                            variant: "error",
                        });
                    },
                },
            );
        }
    };

    return (
        <List>
            {filteredDocuments.map((media) => {
                const fileExtension = media.filename
                    ?.substring(media.filename?.lastIndexOf(".") + 1)
                    .toLowerCase();

                const isVideo = ["mp4", "mov", "avi", "mkv", "webm", "mpeg", "mpg"].includes(
                    fileExtension || "",
                );
                const isAudio = ["mp3", "wav", "m4a", "aac", "ogg", "flac"].includes(
                    fileExtension || "",
                );

                return (
                    <ListItem
                        key={media.idUUID}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            px: 1,
                            borderBottom: "1px solid #ccc",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "flex-start",
                                justifyContent: "center",
                                width: "100%",
                            }}
                        >
                            <ListItemButton
                                onClick={() => {
                                    setMediaUUID(media.idUUID);
                                }}
                            >
                                {fileExtension === "pdf" && (
                                    <ListItemIcon sx={{ height: "100%" }}>
                                        <PictureAsPdfIcon color="action" />
                                    </ListItemIcon>
                                )}

                                {(fileExtension === "doc" || fileExtension === "docx") && (
                                    <ListItemIcon sx={{ height: "100%" }}>
                                        <InsertDriveFileIcon color="action" />
                                    </ListItemIcon>
                                )}

                                {isVideo && (
                                    <ListItemIcon sx={{ height: "100%" }}>
                                        <VideoFileIcon color="action" />
                                    </ListItemIcon>
                                )}

                                {isAudio && (
                                    <ListItemIcon sx={{ height: "100%" }}>
                                        <AudioFileIcon color="action" />
                                    </ListItemIcon>
                                )}

                                {fileExtension &&
                                    !["pdf", "doc", "docx"].includes(fileExtension) &&
                                    !isVideo &&
                                    !isAudio && (
                                        <ListItemIcon sx={{ height: "100%" }}>
                                            <InsertDriveFileIcon color="action" />
                                        </ListItemIcon>
                                    )}

                                <ListItemText primary={media.filename} />
                                {media.idUUID === mediaUUID && loading && <CircularProgress />}
                                {showDelete &&
                                    media.idUUID &&
                                    hasPermission("delete:attachment") && (
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteMedia(media.idUUID);
                                            }}
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                            </ListItemButton>
                        </Box>
                        <ListItemText
                            primary={
                                <Box sx={{ fontSize: "12px", fontStyle: "italic" }}>
                                    {`${dayjs(media.metaModifiedWhen).format(
                                        "MMM D YYYY - hh:mma",
                                    )} ${media.tag}`}
                                </Box>
                            }
                        />
                    </ListItem>
                );
            })}
        </List>
    );
};

export default MediaDocumentsListRaw;
